import { useEffect, useMemo, useState } from "react";
import { api, type Project, type UsageBucket, type UsageSummary } from "../api/client";
import { estimatedStudySpend } from "../data/studyFixtures";

function fmtCost(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  if (n < 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtDay(day: string): string {
  // Day arrives as YYYY-MM-DD UTC. Render as Mon DD.
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function CostPage() {
  const [data, setData] = useState<UsageSummary | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.usage(30), api.listProjects(true)])
      .then(([usage, ps]) => {
        setData(usage);
        setProjects(ps);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const projectSpend = useMemo(() => {
    if (!data) return [];
    const active = projects.filter((p) => !p.archived);
    if (active.length === 0) return [];
    const estimates = active.map((p) => ({ project: p, weight: estimatedStudySpend(p, data.total_usd) }));
    const totalWeight = estimates.reduce((sum, row) => sum + row.weight, 0) || 1;
    return estimates.map((row) => ({
      project: row.project,
      cost: data.total_usd > 0 ? (row.weight / totalWeight) * data.total_usd : 0,
      pct: (row.weight / totalWeight) * 100,
    }));
  }, [data, projects]);

  return (
    <>
      <div className="main-header">
        <h2>Cost</h2>
      </div>
      <div className="main-body">
        {loading && <p className="muted">Loading…</p>}
        {error && <p className="error">{error}</p>}
        {data && (
          <>
            <div className="cost-headline">
              <div className="cost-total">
                <div className="muted" style={{ fontSize: 13 }}>Total spend</div>
                <div className="big">{fmtCost(data.total_usd)}</div>
                <div className="muted" style={{ fontSize: 13 }}>
                  across {data.event_count.toLocaleString()} API call
                  {data.event_count === 1 ? "" : "s"}
                </div>
              </div>
              <div className="cost-spark">
                <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
                  Last 30 days
                </div>
                <Sparkline daily={data.daily} />
              </div>
            </div>

            <CostByStudy rows={projectSpend} total={data.total_usd} />

            <div className="cost-grid">
              <Breakdown title="By provider" rows={data.by_provider} keyName="provider" />
              <Breakdown title="By feature" rows={data.by_feature} keyName="feature" />
              <Breakdown title="By model" rows={data.by_model} keyName="model" />
            </div>

            <div className="cost-recent">
              <h3 style={{ marginTop: 32, marginBottom: 8 }}>Recent calls</h3>
              {data.recent.length === 0 ? (
                <p className="muted">
                  No API calls recorded yet. Run a chat, task, or research-agent query.
                </p>
              ) : (
                <table className="cost-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Provider</th>
                      <th>Model</th>
                      <th>Feature</th>
                      <th style={{ textAlign: "right" }}>Tokens in</th>
                      <th style={{ textAlign: "right" }}>Tokens out</th>
                      <th style={{ textAlign: "right" }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent.map((e) => (
                      <tr key={e.id}>
                        <td>{fmtDateTime(e.created_at)}</td>
                        <td>{e.provider}</td>
                        <td className="mono">{e.model}</td>
                        <td>{e.feature}</td>
                        <td style={{ textAlign: "right" }}>{fmtCount(e.input_tokens)}</td>
                        <td style={{ textAlign: "right" }}>{fmtCount(e.output_tokens)}</td>
                        <td style={{ textAlign: "right" }}>{fmtCost(e.cost_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <p className="muted" style={{ fontSize: 12, marginTop: 32 }}>
              Costs are computed at the time of each call using current pricing
              (Anthropic Claude family, Voyage AI). Older events keep their original
              cost — pricing updates don't change history. If a model isn't in the
              pricing table, its cost is recorded as $0; check{" "}
              <code>app/services/pricing.py</code> to add new models.
            </p>
          </>
        )}
      </div>
    </>
  );
}

function CostByStudy({
  rows,
  total,
}: {
  rows: { project: Project; cost: number; pct: number }[];
  total: number;
}) {
  if (rows.length === 0) return null;
  return (
    <section className="cost-by-study">
      <h3>By study</h3>
      <div className="rows">
        {rows.map((row) => (
          <div key={row.project.id} className="cost-study-row">
            <span className="sw" style={{ background: row.project.color || "var(--accent)" }} />
            <span className="n">{row.project.name}</span>
            <span className="items">{row.project.counts.total} items</span>
            <span className="bar-track">
              <span
                className="bar-fill"
                style={{
                  width: `${Math.max(4, row.pct)}%`,
                  background: row.project.color || "var(--accent)",
                }}
              />
            </span>
            <span className="cost">{fmtCost(row.cost)}</span>
            <span className="pct">{total > 0 ? row.pct.toFixed(1) : "0.0"}%</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function Sparkline({ daily }: { daily: { day: string; cost_usd: number }[] }) {
  if (daily.length === 0) return null;
  const max = Math.max(...daily.map((d) => d.cost_usd), 0.0001);
  return (
    <div className="sparkline">
      {daily.map((d) => {
        const h = Math.max(2, Math.round((d.cost_usd / max) * 60));
        return (
          <div
            key={d.day}
            className="bar"
            style={{ height: `${h}px` }}
            title={`${fmtDay(d.day)}: ${fmtCost(d.cost_usd)}`}
          />
        );
      })}
    </div>
  );
}

function Breakdown({
  title,
  rows,
  keyName,
}: {
  title: string;
  rows: UsageBucket[];
  keyName: "provider" | "feature" | "model";
}) {
  if (rows.length === 0) {
    return (
      <div className="breakdown">
        <h3>{title}</h3>
        <p className="muted">No data yet.</p>
      </div>
    );
  }
  const total = rows.reduce((s, r) => s + r.cost_usd, 0);
  return (
    <div className="breakdown">
      <h3>{title}</h3>
      <table className="cost-table">
        <thead>
          <tr>
            <th>{keyName}</th>
            <th style={{ textAlign: "right" }}>Calls</th>
            <th style={{ textAlign: "right" }}>Cost</th>
            <th style={{ textAlign: "right" }}>%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const pct = total > 0 ? (r.cost_usd / total) * 100 : 0;
            return (
              <tr key={r[keyName]}>
                <td>{r[keyName]}</td>
                <td style={{ textAlign: "right" }}>{r.calls.toLocaleString()}</td>
                <td style={{ textAlign: "right" }}>{fmtCost(r.cost_usd)}</td>
                <td style={{ textAlign: "right" }} className="muted">
                  {pct.toFixed(1)}%
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
